import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';
import FlashNotification from './FlashNotification';
import AuthLayout from './auth/AuthLayout';
import { Button, Input, Label } from './ui';

const Login = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const { login, checkAuth } = React.useContext(AuthContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Renvoyé ici parce que l'école a été suspendue pendant la session
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get('suspended')) {
            setNotification({ type: 'error', message: t('login.suspended') });
        }
    }, [t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await login(phoneNumber, password);
            await checkAuth();
            setNotification({ type: 'success', message: t('login.success') });

            // Super admin redirect
            if (response.user?.isSuperAdmin) {
              navigate('/superadmin/dashboard');
            } else {
              navigate('/dashboard');
            }
        } catch (error) {
            console.error('Login failed', error);
            setNotification({ type: 'error', message: t(error.response?.data?.code === 'SCHOOL_SUSPENDED' ? 'login.suspended' : 'login.error') });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title={t('login.title')}
            footer={
                <button
                    type="button"
                    className="font-medium text-brand-ink hover:underline"
                    onClick={() => navigate('/forgot-password')}
                >
                    {t('login.forgotPassword')}
                </button>
            }
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                    <Label htmlFor="phone-number">{t('login.phoneNumber')}</Label>
                    <Input
                        id="phone-number"
                        name="phoneNumber"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        required
                        dir="ltr"
                        className="rtl:text-right"
                        pattern="^[342]\d{7}$"
                        title={t('login.phoneNumberPattern')}
                        placeholder="4X XX XX XX"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                </div>

                <div>
                    <Label htmlFor="password">{t('login.password')}</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            required
                            placeholder="••••••••"
                            className="pe-10"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                            className="absolute inset-y-0 end-0 flex items-center pe-3 text-ink-subtle transition-colors hover:text-ink"
                        >
                            {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <Button type="submit" size="lg" loading={loading} className="w-full">
                    {t('login.submit')}
                </Button>
            </form>

            {notification && (
                <FlashNotification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </AuthLayout>
    );
};

export default Login;
